<template>
    <div ref="rootElement" class="zoning-plan-layer" style="display: none;"></div>
</template>

<script lang="ts">
import {
    fetchAreas,
    findHitFeature,
    LAYER_STYLES,
    overlayStyle,
    landUseStyle,
    popupContent,
    toFeatureCollection,
    USAGE_COLORS,
    ZoningArea,
} from '@/components/maps/zoningPlanAreas';
import {acquireLeafletMap, AcquiredMap} from '@/shared/leafletMapRegistry';
import {registerMinZoom, unregisterMinZooms} from '@/shared/layersControlMinZoom';
import L from 'leaflet';
import {defineComponent, onBeforeUnmount, onMounted, ref} from 'vue';

interface LayerConfig {
    readonly key: string;
    readonly label: string;
    readonly defaultVisible: boolean;
}

// Backend layer keys (ZoningPlanLayer enum values) and their overlay labels.
const LAYERS: readonly LayerConfig[] = [
    {key: 'land_use', label: 'Plochy s rozdílným způsobem využití', defaultVisible: false},
    {key: 'built_up_area', label: 'Zastavěné území', defaultVisible: false},
    {key: 'public_benefit', label: 'Veřejně prospěšné stavby a opatření', defaultVisible: false},
    {key: 'ecological_stability', label: 'ÚSES', defaultVisible: false},
    {key: 'territorial_reserve', label: 'Územní rezervy', defaultVisible: false},
    {key: 'corridor', label: 'Koridory', defaultVisible: false},
];

const PANE_NAME = 'zoningPlan';
const PANE_Z_INDEX = '240';
const BOUNDS_PADDING = 0.25;

// Areas are only shown once zoomed in enough, matching the transactions layer (PointsMap).
const MIN_ZOOM = 14;

interface ManagedLayer {
    readonly config: LayerConfig;
    readonly geoJson: L.GeoJSON;
    loadedBounds: L.LatLngBounds | null;
    abortController: AbortController | null;
}

export default defineComponent({
    props: {
        loadAreasUrl: {
            type: String,
            required: true,
        },
        planDetailUrl: {
            type: String,
            required: true,
        },
        // 'true' for admins: technical ids and vectorization quality in the popup (map-side QA).
        debugInfo: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const rootElement = ref<HTMLElement | null>(null);

        let map: L.Map | null = null;
        let layersControl: L.Control.Layers | null = null;
        let legend: L.Control | null = null;
        const managedLayers: ManagedLayer[] = [];

        // Layer ids of the overlays this component added, so a "Územní plán" header can be inserted
        // above them in the layers control (like the flood-zone layer does).
        const overlayLayerIds = new Set<number>();
        let originalControlUpdate: (() => unknown) | null = null;

        function createManagedLayer(config: LayerConfig): ManagedLayer {
            // Non-interactive (clicks are handled at the map level, see handleMapClick) — keeps the
            // thousands of polygons light and lets the popup combine all layers at the clicked point.
            const geoJson = L.geoJSON(undefined, {
                pane: PANE_NAME,
                interactive: false,
                style: (feature) => {
                    const area = feature?.properties as ZoningArea | undefined;
                    if (config.key === 'land_use' && area) {
                        return landUseStyle(area);
                    }
                    return area ? overlayStyle(area) : LAYER_STYLES[config.key] ?? {};
                },
            });

            return {config, geoJson, loadedBounds: null, abortController: null};
        }

        function handleMapClick(event: L.LeafletMouseEvent): void {
            if (!map) {
                return;
            }
            const contents: string[] = [];
            for (const managed of managedLayers) {
                if (!map.hasLayer(managed.geoJson)) {
                    continue;
                }
                const feature = findHitFeature(map, managed.geoJson, event.latlng);
                if (feature?.properties) {
                    contents.push(popupContent(feature.properties as ZoningArea, props.planDetailUrl, props.debugInfo === 'true'));
                }
            }
            if (contents.length === 0) {
                return;
            }
            L.popup({className: 'zoning-plan-popup'})
                .setLatLng(event.latlng)
                .setContent(contents.join('<hr class="zoning-plan-popup__sep"/>'))
                .openOn(map);
        }

        async function loadLayer(managed: ManagedLayer): Promise<void> {
            if (!map || !map.hasLayer(managed.geoJson)) {
                return;
            }

            if (map.getZoom() < MIN_ZOOM) {
                managed.geoJson.clearLayers();
                managed.loadedBounds = null;
                return;
            }

            const bounds = map.getBounds();
            if (managed.loadedBounds && managed.loadedBounds.contains(bounds)) {
                return;
            }

            const paddedBounds = bounds.pad(BOUNDS_PADDING);
            managed.abortController?.abort();
            const abortController = new AbortController();
            managed.abortController = abortController;

            try {
                const areas = await fetchAreas(props.loadAreasUrl, managed.config.key, paddedBounds, abortController.signal);

                if (areas === null) {
                    return;
                }

                const collection = toFeatureCollection(areas);

                managed.geoJson.clearLayers();
                managed.geoJson.addData(collection);
                managed.loadedBounds = paddedBounds;
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    managed.loadedBounds = null;
                }
            }
        }

        function loadVisibleLayers(): void {
            updateLegendVisibility();
            for (const managed of managedLayers) {
                void loadLayer(managed);
            }
        }

        // Land-use categories shown in the legend (matches the LandUsageType enum).
        const LEGEND_ITEMS: ReadonlyArray<readonly [string, string]> = [
            ['residential', 'Bydlení'],
            ['mixed_residential', 'Smíšené obytné'],
            ['civic_amenities', 'Občanské vybavení'],
            ['recreation', 'Rekreace'],
            ['production', 'Výroba'],
            ['transport_infrastructure', 'Dopravní infrastruktura'],
            ['technical_infrastructure', 'Technická infrastruktura'],
            ['public_spaces', 'Veřejná prostranství'],
            ['greenery', 'Zeleň'],
            ['water', 'Vodní'],
            ['agricultural', 'Zemědělská'],
            ['forest', 'Lesní'],
            ['natural', 'Přírodní'],
            ['mixed_open_landscape', 'Smíšené nezastavěného území'],
            ['mining', 'Těžba nerostů'],
            ['specific', 'Specifické'],
        ];

        function buildLegend(): L.Control {
            const control = new L.Control({position: 'bottomright'});
            control.onAdd = () => {
                const container = L.DomUtil.create('div', 'zoning-plan-legend');
                const items = LEGEND_ITEMS
                    .map(([key, label]) => '<div class="zoning-plan-legend__item">'
                        + `<span class="zoning-plan-legend__swatch" style="background:${USAGE_COLORS[key]}"></span>${label}</div>`)
                    .join('');
                container.innerHTML = `<div class="zoning-plan-legend__title">Územní plán</div>${items}`;
                return container;
            };
            return control;
        }

        function updateLegendVisibility(): void {
            if (!map || !legend) {
                return;
            }
            const landUseLayer = managedLayers.find((managed) => managed.config.key === 'land_use');
            const visible = landUseLayer !== undefined
                && map.hasLayer(landUseLayer.geoJson)
                && map.getZoom() >= MIN_ZOOM;
            if (visible) {
                legend.addTo(map);
            } else {
                legend.remove();
            }
        }

        // Inserts a "Územní plán" header before this component's overlays in the layers control.
        function injectHeader(): void {
            const overlaysList = (layersControl as unknown as {_overlaysList?: HTMLElement})._overlaysList;
            if (!overlaysList) {
                return;
            }
            let firstLabel: HTMLElement | null = null;
            overlaysList.querySelectorAll('label').forEach((label) => {
                if (firstLabel) {
                    return;
                }
                const input = label.querySelector('input') as unknown as {layerId?: number} | null;
                if (input && input.layerId !== undefined && overlayLayerIds.has(input.layerId)) {
                    firstLabel = label;
                }
            });
            if (firstLabel) {
                const header = document.createElement('div');
                header.className = 'zoning-plan-header';
                header.textContent = 'Územní plán';
                overlaysList.insertBefore(header, firstLabel);
            }
        }

        // The layers control rebuilds its list on every change, so re-inject the header after each.
        function patchControlUpdate(): void {
            const control = layersControl as unknown as {_update?: () => unknown};
            if (!control._update) {
                return;
            }
            originalControlUpdate = control._update;
            control._update = (): unknown => {
                const result = originalControlUpdate?.call(layersControl);
                injectHeader();
                return result;
            };
        }

        function restoreControlUpdate(): void {
            if (originalControlUpdate) {
                (layersControl as unknown as {_update?: () => unknown})._update = originalControlUpdate;
                originalControlUpdate = null;
            }
        }

        function initLayers(): void {
            if (!map) {
                return;
            }

            const pane = map.createPane(PANE_NAME);
            pane.style.zIndex = PANE_Z_INDEX;

            for (const config of LAYERS) {
                const managed = createManagedLayer(config);
                managedLayers.push(managed);
                layersControl?.addOverlay(managed.geoJson, config.label);
                overlayLayerIds.add(L.Util.stamp(managed.geoJson));
                if (layersControl) {
                    registerMinZoom(map, layersControl, managed.geoJson, MIN_ZOOM);
                }
                if (config.defaultVisible) {
                    managed.geoJson.addTo(map);
                }
            }

            patchControlUpdate();
            injectHeader();

            legend = buildLegend();
            updateLegendVisibility();

            map.on('moveend', loadVisibleLayers);
            map.on('overlayadd', onOverlayChange);
            map.on('overlayremove', onOverlayChange);
            map.on('click', handleMapClick);

            loadVisibleLayers();
        }

        function onOverlayChange(): void {
            updateLegendVisibility();
            loadVisibleLayers();
        }

        function detachFromMap(): void {
            if (map) {
                restoreControlUpdate();
                if (layersControl) {
                    unregisterMinZooms(layersControl, managedLayers.map((managed) => managed.geoJson));
                }
                map.off('moveend', loadVisibleLayers);
                map.off('overlayadd', onOverlayChange);
                map.off('overlayremove', onOverlayChange);
                map.off('click', handleMapClick);
                for (const managed of managedLayers) {
                    managed.abortController?.abort();
                    map.removeLayer(managed.geoJson);
                }
                legend?.remove();
            }
            managedLayers.length = 0;
            overlayLayerIds.clear();
            legend = null;
            layersControl = null;
            map = null;
        }

        function attachToMap(acquired: AcquiredMap): void {
            map = acquired.map;
            layersControl = acquired.layersControl;
            initLayers();
        }

        let cleanupMapListener: (() => void) | null = null;

        onMounted(() => {
            cleanupMapListener = acquireLeafletMap(rootElement.value!, attachToMap);
        });

        onBeforeUnmount(() => {
            cleanupMapListener?.();
            detachFromMap();
        });

        return {rootElement};
    },
});
</script>
